import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { TimePickerTrigger } from '@/components/ui/time-picker';
import { storeAPI, appointmentAPI } from '../lib/api';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  ArrowLeft,
  Scissors,
  Clock,
  User,
  Calendar as CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';

export default function BookingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { store, selectStore } = useStore();
  const { user, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [guestInfo, setGuestInfo] = useState({ name: '', phone: '', notes: '' });

  useEffect(() => {
    loadData();
  }, [slug]);

  // Quando si seleziona un servizio, carica lo staff disponibile
  useEffect(() => {
    if (selectedService) {
      loadAvailableStaff(selectedService.id);
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedService && selectedStaff && selectedDate) {
      loadSlots();
    }
  }, [selectedService, selectedStaff, selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesRes, staffRes] = await Promise.all([
        storeAPI.publicServices(slug),
        storeAPI.publicStaff(slug)
      ]);

      setServices(servicesRes.data || []);
      setStaff(staffRes.data || []);

      // Pre-select service from URL
      const serviceId = searchParams.get('service');
      if (serviceId) {
        const service = (servicesRes.data || []).find(s => s.id === serviceId);
        if (service) {
          setSelectedService(service);
          setStep(2);
        }
      }
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableStaff = async (serviceId) => {
    try {
      const response = await storeAPI.publicStaff(slug);
      const staffForService = response.data || [];
      setAvailableStaff(staffForService);

      if (selectedStaff && !staffForService.find(s => s.id === selectedStaff.id)) {
        setSelectedStaff(null);
      }
    } catch (error) {
      setAvailableStaff([]);
    }
  };

  const loadSlots = async () => {
    try {
      setLoadingSlots(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await appointmentAPI.getSlots(selectedStaff.id, dateStr, selectedService.id);
      setAvailableSlots(response.data.slots || []);
      setSelectedTime(null); // Reset time when date/staff changes
      
      if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Errore caricamento slot:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedStaff || !selectedDate || !selectedTime) {
      toast.error('Completa tutti i campi');
      return;
    }
    
    if (!isAuthenticated && (!guestInfo.name || !guestInfo.phone)) {
      toast.error('Nome e telefono sono obbligatori per prenotazioni ospite');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        service_id: selectedService.id,
        staff_id: selectedStaff.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        notes: guestInfo.notes || null,
      };
      
      if (!isAuthenticated) {
        data.guest_name = guestInfo.name;
        data.guest_phone = guestInfo.phone;
      }
      
      await appointmentAPI.create(data);
      toast.success('Prenotazione effettuata con successo!');
      
      // Redirect to confirmation or dashboard
      if (isAuthenticated) {
        navigate('/dashboard');
      } else {
        navigate(`/s/${slug}`);
      }
    } catch (error) {
      console.error('Errore prenotazione:', error);
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}`} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Scissors className="w-6 h-6 text-primary" />
              <span className="font-semibold">{store?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main max-w-4xl">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[
              { num: 1, label: 'Servizio' },
              { num: 2, label: 'Operatore' },
              { num: 3, label: 'Data e Ora' },
              { num: 4, label: 'Conferma' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div 
                  className={`flex flex-col items-center ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}
                  onClick={() => step > s.num && setStep(s.num)}
                  style={{ cursor: step > s.num ? 'pointer' : 'default' }}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    step >= s.num ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block">{s.label}</span>
                </div>
                {i < 3 && (
                  <div className={`w-12 sm:w-24 h-0.5 mx-2 transition-all ${step > s.num ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Service Selection */}
          {step === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-2 text-center">Scegli il Servizio</h2>
              <p className="text-muted-foreground text-center mb-8">Seleziona il servizio che desideri prenotare</p>
              
              {services.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">Nessun servizio disponibile</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map((service) => (
                    <Card 
                      key={service.id}
                      className={`cursor-pointer transition-all ${
                        selectedService?.id === service.id 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedService(service)}
                      data-testid={`select-service-${service.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{service.name}</h3>
                              {selectedService?.id === service.id && (
                                <CheckCircle2 className="w-5 h-5 text-primary" />
                              )}
                            </div>
                            {service.description && (
                              <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                            )}
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                              <Clock className="w-4 h-4" />
                              <span>{service.duration_minutes} min</span>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-primary">{formatPrice(service.price)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end mt-8">
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!selectedService}
                  data-testid="next-step-1"
                >
                  Continua
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Staff Selection */}
          {step === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-2 text-center">Scegli l&apos;Operatore</h2>
              <p className="text-muted-foreground text-center mb-8">
                {availableStaff.length < staff.length 
                  ? `${availableStaff.length} operatori disponibili per "${selectedService?.name}"`
                  : 'Con chi vuoi prenotare?'
                }
              </p>
              
              {availableStaff.length === 0 ? (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">Nessun operatore disponibile per questo servizio</p>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableStaff.map((member) => (
                    <Card 
                      key={member.id}
                      className={`cursor-pointer transition-all ${
                        selectedStaff?.id === member.id 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedStaff(member)}
                      data-testid={`select-staff-${member.id}`}
                    >
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center relative">
                          <User className="w-8 h-8 text-primary" />
                          {selectedStaff?.id === member.id && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold">{member.user?.name || 'Operatore'}</h3>
                        {member.specializations?.length > 0 && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {member.specializations.join(', ')}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="prev-step-2">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  disabled={!selectedStaff}
                  data-testid="next-step-2"
                >
                  Continua
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Date & Time Selection - WHEEL PICKER */}
          {step === 3 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-2 text-center">Scegli Data e Ora</h2>
              <p className="text-muted-foreground text-center mb-8">Quando vuoi venire?</p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Date Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5" />
                      Seleziona la Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }}
                      disabled={(date) => date < new Date() || date.getDay() === 0}
                      locale={it}
                      className="rounded-md border"
                      data-testid="booking-calendar"
                    />
                  </CardContent>
                </Card>
                
                {/* Time Selection - Using Wheel Time Picker */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Seleziona l&apos;Orario
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedDate ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Seleziona prima una data</p>
                      </div>
                    ) : loadingSlots ? (
                      <div className="text-center py-8">
                        <div className="spinner mx-auto mb-2" />
                        <p className="text-muted-foreground">Caricamento orari...</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <TimePickerTrigger
                          availableSlots={availableSlots}
                          value={selectedTime}
                          onChange={setSelectedTime}
                          placeholder="Tocca per scegliere"
                        />
                        
                        {selectedTime && (
                          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                            <p className="text-sm text-muted-foreground">Orario selezionato:</p>
                            <p className="text-lg font-semibold text-primary">{selectedTime}</p>
                          </div>
                        )}

                        {availableSlots.length > 0 && (
                          <p className="text-xs text-muted-foreground text-center">
                            {availableSlots.length} orari disponibili
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="prev-step-3">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <Button 
                  onClick={() => setStep(4)} 
                  disabled={!selectedDate || !selectedTime}
                  data-testid="next-step-3"
                >
                  Continua
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-2 text-center">Conferma Prenotazione</h2>
              <p className="text-muted-foreground text-center mb-8">Verifica i dettagli e conferma</p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Booking Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Riepilogo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Servizio</span>
                      <span className="font-medium">{selectedService?.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Operatore</span>
                      <span className="font-medium">{selectedStaff?.user?.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Data</span>
                      <span className="font-medium">
                        {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Ora</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Durata</span>
                      <span className="font-medium">{selectedService?.duration_minutes} min</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Prezzo</span>
                      <span className="font-bold text-primary text-lg">
                        {formatPrice(selectedService?.price)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Guest Info / Auth */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {isAuthenticated ? 'I Tuoi Dati' : 'Dati di Contatto'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isAuthenticated ? (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-muted-foreground">Nome</Label>
                          <p className="font-medium">{user?.name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Email</Label>
                          <p className="font-medium">{user?.email}</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notes">Note (opzionale)</Label>
                          <Textarea
                            id="notes"
                            placeholder="Eventuali richieste o note..."
                            value={guestInfo.notes}
                            onChange={(e) => setGuestInfo({ ...guestInfo, notes: e.target.value })}
                            data-testid="booking-notes"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="guestName">Nome *</Label>
                          <Input
                            id="guestName"
                            placeholder="Il tuo nome"
                            value={guestInfo.name}
                            onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                            required
                            data-testid="guest-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="guestPhone">Telefono *</Label>
                          <Input
                            id="guestPhone"
                            type="tel"
                            placeholder="+39 123 456 7890"
                            value={guestInfo.phone}
                            onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                            required
                            data-testid="guest-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="guestNotes">Note (opzionale)</Label>
                          <Textarea
                            id="guestNotes"
                            placeholder="Eventuali richieste o note..."
                            value={guestInfo.notes}
                            onChange={(e) => setGuestInfo({ ...guestInfo, notes: e.target.value })}
                            data-testid="guest-notes"
                          />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Hai un account?{' '}
                          <Link to="/login" className="text-primary hover:underline">
                            Accedi
                          </Link>
                          {' '}per gestire le tue prenotazioni.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={() => setStep(3)} data-testid="prev-step-4">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  size="lg"
                  data-testid="confirm-booking"
                >
                  {submitting ? 'Prenotazione in corso...' : 'Conferma Prenotazione'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
